import React, { useMemo } from 'react';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { XeroInvoice } from '../../../types/xero';
import { parseXeroDate } from '../../../utils/xeroDate';

interface ExpenseBreakdownViewProps {
  bills: XeroInvoice[];
  startDate: Date;
  endDate: Date;
}

interface SupplierExpense {
  name: string;
  amount: number;
  billCount: number;
  pct: number;
}

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);

const CHART_COLORS = ['#F1416C', '#FFA800', '#7239EA', '#50CD89', '#009EF7', '#FFC700', '#17C653', '#5014D0'];

export function ExpenseBreakdownView({ bills, startDate, endDate }: ExpenseBreakdownViewProps) {
  const { suppliers, totalExpenses, uniqueSupplierCount } = useMemo(() => {
    const expenseMap: Record<string, { amount: number; count: number }> = {};

    bills.forEach((bill) => {
      if (bill.Status === 'VOIDED') return;
      const contactName = bill.Contact?.Name || 'Unknown';
      if (bill.AmountPaid <= 0) return;

      if (bill.Payments && bill.Payments.length > 0) {
        bill.Payments.forEach(p => {
          const payDate = parseXeroDate(p.Date);
          if (payDate && payDate >= startDate && payDate <= endDate) {
            if (!expenseMap[contactName]) expenseMap[contactName] = { amount: 0, count: 0 };
            expenseMap[contactName].amount += p.Amount;
            expenseMap[contactName].count++;
          }
        });
      } else {
        const billDate = parseXeroDate((bill as any).Date || (bill as any).DateString);
        if (billDate && billDate >= startDate && billDate <= endDate) {
          if (!expenseMap[contactName]) expenseMap[contactName] = { amount: 0, count: 0 };
          expenseMap[contactName].amount += bill.AmountPaid;
          expenseMap[contactName].count++;
        }
      }
    });

    const total = Object.values(expenseMap).reduce((s, v) => s + v.amount, 0);
    const sorted: SupplierExpense[] = Object.entries(expenseMap)
      .map(([name, data]) => ({
        name,
        amount: data.amount,
        billCount: data.count,
        pct: total > 0 ? (data.amount / total) * 100 : 0,
      }))
      .sort((a, b) => b.amount - a.amount);

    return { suppliers: sorted, totalExpenses: total, uniqueSupplierCount: sorted.length };
  }, [bills, startDate, endDate]);

  const topN = suppliers.slice(0, 8);
  const otherAmount = suppliers.slice(8).reduce((s, c) => s + c.amount, 0);

  const chartOptions: ApexOptions = {
    chart: {
      type: 'bar',
      toolbar: { show: false },
      fontFamily: 'inherit',
    },
    plotOptions: {
      bar: {
        horizontal: true,
        borderRadius: 4,
        barHeight: '65%',
        distributed: true,
      },
    },
    colors: CHART_COLORS,
    dataLabels: {
      enabled: true,
      formatter: (val) => formatCurrency(val as number),
      style: { fontSize: '11px', fontWeight: 600, colors: ['#fff'] },
      offsetX: 5,
    },
    xaxis: {
      labels: {
        formatter: (val) => {
          const n = Number(val);
          if (Math.abs(n) >= 1000) {
            return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', notation: 'compact', maximumFractionDigits: 1 }).format(n);
          }
          return formatCurrency(n);
        },
        style: { colors: 'var(--bs-gray-600)', fontSize: '11px' },
      },
    },
    yaxis: {
      labels: {
        style: { colors: 'var(--bs-gray-700)', fontSize: '12px' },
        maxWidth: 140,
      },
    },
    legend: { show: false },
    tooltip: {
      theme: 'dark',
      custom: ({ dataPointIndex }) => {
        const s = topN[dataPointIndex];
        if (!s) return '';
        return `<div class="px-3 py-2">
          <div class="fw-bold mb-1">${s.name}</div>
          <div>Expenses: ${formatCurrency(s.amount)}</div>
          <div class="text-muted fs-8">${s.billCount} payment${s.billCount !== 1 ? 's' : ''} · ${s.pct.toFixed(1)}% of total</div>
        </div>`;
      },
    },
    grid: {
      borderColor: 'var(--bs-gray-200)',
      strokeDashArray: 4,
      xaxis: { lines: { show: true } },
      yaxis: { lines: { show: false } },
    },
    states: {
      hover: { filter: { type: 'darken', value: 0.9 } },
      active: { filter: { type: 'none' } },
    },
  };

  const chartSeries = [{
    name: 'Expenses',
    data: topN.map(s => Math.round(s.amount * 100) / 100),
  }];

  if (suppliers.length === 0) {
    return (
      <div className="text-center py-10">
        <i className="bi bi-receipt text-muted fs-2x d-block mb-3" />
        <span className="text-muted fs-6">No expense data in the selected period</span>
      </div>
    );
  }

  const topSupplier = suppliers[0];

  return (
    <div className="row g-4">
      <div className="col-12 col-lg-4">
        <div className="d-flex flex-column gap-4">
          <div>
            <span className="text-muted fs-7 d-block mb-1">Total Expenses</span>
            <div className="fs-2 fw-bold text-danger">{formatCurrency(totalExpenses)}</div>
            <span className="text-muted fs-8">in period</span>
          </div>
          <div>
            <span className="text-muted fs-7 d-block mb-1">Suppliers</span>
            <div className="fs-3 fw-bold text-gray-800">{uniqueSupplierCount}</div>
            <span className="text-muted fs-8">with payments</span>
          </div>
          {topSupplier && (
            <div className="pt-2 border-top">
              <span className="text-muted fs-7 d-block mb-1">Largest Supplier</span>
              <div className="fs-6 fw-bold text-gray-800">{topSupplier.name}</div>
              <div className="d-flex align-items-center gap-2 mt-1">
                <span className="fs-7 fw-semibold text-danger">{formatCurrency(topSupplier.amount)}</span>
                <span className="text-muted fs-8">({topSupplier.pct.toFixed(1)}%)</span>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="col-12 col-lg-8">
        <Chart
          options={{ ...chartOptions, xaxis: { ...chartOptions.xaxis, categories: topN.map(s => s.name) } }}
          series={chartSeries}
          type="bar"
          height={Math.max(200, topN.length * 36)}
        />
        {otherAmount > 0 && (
          <div className="mt-2 text-muted fs-8">
            + {suppliers.length - 8} other supplier{suppliers.length - 8 !== 1 ? 's' : ''}: {formatCurrency(otherAmount)}
          </div>
        )}
      </div>
    </div>
  );
}

export default ExpenseBreakdownView;
